import { useCallback, useMemo } from "react"
import InputField from "components/Common/InputField"
import { Form, FormFeedback, Label } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import {
  buildCustomerTypeOptions,
  buildSuggestionOptions,
  getCustomerTypeFieldMeta,
  resolveCustomerTypeKey,
} from "helpers/customerOnboarding"

const CustomerForm = ({
  initialData = {},
  options = {},
  onSubmit,
  onCancel,
  isEdit = false,
  isLoading = false,
  showAdditionalInfo = true,
  submitButtonText,
  cancelButtonText = "Cancel",
}) => {
  const {
    designations = [],
    companies = [],
    banks = [],
    branches = [],
    customerTypes = [],
  } = options

  const designationOptions = useMemo(
    () => buildSuggestionOptions(designations),
    [designations]
  )
  const companyOptions = useMemo(() => buildSuggestionOptions(companies), [companies])
  const bankOptions = useMemo(() => buildSuggestionOptions(banks), [banks])
  const branchOptions = useMemo(() => buildSuggestionOptions(branches), [branches])
  const customerTypeOptions = useMemo(
    () => buildCustomerTypeOptions(customerTypes),
    [customerTypes]
  )

  const validationSchema = Yup.object({
    name: Yup.string().required("Please enter customer name"),
    mobile: Yup.string().max(20, "Mobile must be at most 20 characters"),
    email: Yup.string().email("Invalid email format").max(255),
    customer_type: Yup.string().required("Please select customer type"),
    designation: Yup.string().nullable().test(
      "designation-by-type",
      "Designation is required for service and banker customers",
      function (value) {
        const customerTypeKey = resolveCustomerTypeKey(this.parent.customer_type)
        if (!["service", "banker"].includes(customerTypeKey)) return true
        return Boolean(String(value || "").trim())
      }
    ),
    company_name: Yup.string().nullable().test(
      "company-by-type",
      "Company name is required for service customers",
      function (value) {
        const customerTypeKey = resolveCustomerTypeKey(this.parent.customer_type)
        if (customerTypeKey !== "service") return true
        return Boolean(String(value || "").trim())
      }
    ),
    bank_name: Yup.string().nullable().test(
      "bank-by-type",
      "Bank name is required for banker customers",
      function (value) {
        const customerTypeKey = resolveCustomerTypeKey(this.parent.customer_type)
        if (customerTypeKey !== "banker") return true
        return Boolean(String(value || "").trim())
      }
    ),
    branch_name: Yup.string().nullable().test(
      "branch-by-type",
      "Branch name is required for banker customers",
      function (value) {
        const customerTypeKey = resolveCustomerTypeKey(this.parent.customer_type)
        if (customerTypeKey !== "banker") return true
        return Boolean(String(value || "").trim())
      }
    ),
    full_address: Yup.string().max(1000),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: initialData?.name || "",
      mobile: initialData?.mobile || "",
      email: initialData?.email || "",
      customer_type: initialData?.customer_type || "general",
      designation: initialData?.designation || "",
      company_name: initialData?.company_name || "",
      bank_name: initialData?.bank_name || "",
      branch_name: initialData?.branch_name || "",
      full_address: initialData?.full_address || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values, formikHelpers) => {
      onSubmit(values, formikHelpers)
    },
  })

  const defaultSubmitText = useMemo(() => {
    return `${isEdit ? "Save Changes" : "Add Customer"}${isLoading ? "..." : ""}`
  }, [isEdit, isLoading])

  const handleSave = useCallback(() => {
    formik.handleSubmit()
  }, [formik])

  const selectedCustomerTypeKey = useMemo(
    () => resolveCustomerTypeKey(formik.values.customer_type),
    [formik.values.customer_type]
  )
  const customerTypeMeta = useMemo(
    () => getCustomerTypeFieldMeta(selectedCustomerTypeKey),
    [selectedCustomerTypeKey]
  )

  const handleCustomerTypeChange = useCallback(
    value => {
      const nextTypeKey = resolveCustomerTypeKey(value)
      formik.setFieldValue("customer_type", value)

      if (nextTypeKey === "general") {
        formik.setFieldValue("designation", "")
        formik.setFieldValue("company_name", "")
        formik.setFieldValue("bank_name", "")
        formik.setFieldValue("branch_name", "")
      }

      if (nextTypeKey === "service") {
        formik.setFieldValue("bank_name", "")
        formik.setFieldValue("branch_name", "")
      }

      if (nextTypeKey === "banker") {
        formik.setFieldValue("company_name", "")
      }
    },
    [formik]
  )

  return (
    <Form>
      <div className="mb-3">
        <Label htmlFor="name">
          Name <span className="text-danger">*</span>
        </Label>
        <InputField
          type="text"
          name="name"
          id="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          invalid={formik.touched.name && !!formik.errors.name}
        />
        {formik.touched.name && formik.errors.name && (
          <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
        )}
      </div>

      <div className="mb-3">
        <Label htmlFor="mobile">Mobile</Label>
        <InputField
          type="text"
          name="mobile"
          id="mobile"
          value={formik.values.mobile}
          onChange={formik.handleChange}
          invalid={formik.touched.mobile && !!formik.errors.mobile}
        />
        {formik.touched.mobile && formik.errors.mobile && (
          <FormFeedback type="invalid">{formik.errors.mobile}</FormFeedback>
        )}
      </div>

      <div className="mb-3">
        <Label htmlFor="email">Email</Label>
        <InputField
          type="email"
          name="email"
          id="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          invalid={formik.touched.email && !!formik.errors.email}
        />
        {formik.touched.email && formik.errors.email && (
          <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
        )}
      </div>

      <div className="mb-3">
        <Label htmlFor="full_address">Full Address</Label>
        <InputField
          type="textarea"
          name="full_address"
          id="full_address"
          value={formik.values.full_address}
          onChange={formik.handleChange}
          invalid={formik.touched.full_address && !!formik.errors.full_address}
        />
        {formik.touched.full_address && formik.errors.full_address && (
          <FormFeedback type="invalid">{formik.errors.full_address}</FormFeedback>
        )}
      </div>

      {showAdditionalInfo && (
        <>
          <div className="mb-3 border-bottom">Customer Onboarding</div>

          <FormikCreatableSelect
            name="customer_type"
            label="Customer Type"
            formik={formik}
            options={customerTypeOptions}
            placeholder="Select or type a customer type"
            required
            onChange={handleCustomerTypeChange}
          />

          {customerTypeMeta.requiresDesignation && (
            <FormikCreatableSelect
              name="designation"
              label="Designation"
              formik={formik}
              options={designationOptions}
              placeholder="Select or type a designation"
              required
            />
          )}

          {selectedCustomerTypeKey === "service" && (
            <FormikCreatableSelect
              name="company_name"
              label="Company Name"
              formik={formik}
              options={companyOptions}
              placeholder="Select or type a company name"
              required
            />
          )}

          {selectedCustomerTypeKey === "banker" && (
            <>
              <FormikCreatableSelect
                name="bank_name"
                label="Bank Name"
                formik={formik}
                options={bankOptions}
                placeholder="Select or type a bank name"
                required
              />
              <FormikCreatableSelect
                name="branch_name"
                label="Branch Name"
                formik={formik}
                options={branchOptions}
                placeholder="Select or type a branch name"
                required
              />
            </>
          )}
        </>
      )}

      <div className="text-end mt-4 pt-3 border-top">
        {onCancel && (
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelButtonText}
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          className="btn btn-success"
          disabled={isLoading}
        >
          {submitButtonText || defaultSubmitText}
        </button>
      </div>
    </Form>
  )
}

export default CustomerForm
